import cn from 'classnames'
import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import AlertCircleIcon from 'ext-ui/svg/icon/AlertCircleIcon'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import React, { ReactElement } from 'react'
import { useDispatch } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { UnassignCardFeedCardSuccessToastMessage } from 'src/app/Toast/Toast'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import MaskedCardNumber from 'src/components/MaskedCardNumber/MaskedCardNumber'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { peopleInvitedToOrgUrl } from 'src/navigation/urls'
import {
  assignedUserDisplayName,
  CardFeedCard,
  toCardFeedAvatarUser,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { useUnassignCardFeedCard } from 'src/queries/cardFeeds/domain/cardFeeds.queries'

import style from './ConfirmUnassignCardModal.style'

export type ConfirmUnassignCardModalProps = BaseModalProps & {
  card: CardFeedCard
  cardFeedId: string
}

const ConfirmUnassignCardModal = (
  props: ConfirmUnassignCardModalProps,
): ReactElement => {
  const { className = '', isOpen, onClose, card, cardFeedId } = props

  const translate = useTranslate()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const unassignCard = useUnassignCardFeedCard()

  const cardholder =
    assignedUserDisplayName(card.assignedUser) ?? translate('this cardholder')
  const avatarUser = card.assignedUser
    ? toCardFeedAvatarUser(card.assignedUser)
    : null

  const handleClose = (): void => {
    onClose()
    unassignCard.reset()
  }

  const handleConfirm = async (): Promise<void> => {
    try {
      await unassignCard.mutateAsync({ cardFeedId, creditCardId: card.id })
      analytics.track('card feed details', { action: 'card unassigned' })
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <UnassignCardFeedCardSuccessToastMessage
              user={cardholder}
              cardName={card.cardName}
            />
          ),
        }),
      )
      handleClose()
    } catch {
      analytics.track('card feed details', {
        action: 'card unassigned failure',
      })
      // Error surfaced via the inline AlertBanner below; keep the modal open.
    }
  }

  const errorMessage = unassignCard.isError
    ? (unassignCard.error?.response?.data?.error ??
      translate(
        'Something went wrong unassigning this card. Please try again.',
      ))
    : undefined

  return (
    <BaseModal
      className={cn('ConfirmUnassignCardModal', className)}
      variant="popup"
      isOpen={isOpen}
      onClose={handleClose}
      css={style.modal}
      aria-label={translate('Unassign Card Holder')}
    >
      <h2 css={style.title}>{translate('Unassign Card Holder')}</h2>
      <p css={style.subtitle}>
        {translate(
          'Unassign this cardholder from the card account listed below? This will only remove their access to this card account.',
        )}
      </p>

      <section css={style.summaryCard}>
        <div css={style.identity}>
          {avatarUser && (
            // The adjacent text already announces the name, so mark the
            // avatar decorative so screen readers don't repeat it.
            <UserAvatar size={AvatarSizes.Large} user={avatarUser} decorative />
          )}
          <div css={style.identityText}>
            {avatarUser?.hasFullName && card.assignedUser?.email ? (
              <>
                <span css={style.identityName}>
                  {avatarUser.fullNameOrEmail}
                </span>
                <span css={style.identityEmail}>{card.assignedUser.email}</span>
              </>
            ) : (
              <span css={style.identityName}>{cardholder}</span>
            )}
          </div>
        </div>
        <div css={style.divider} />
        <span css={style.caption}>{translate('Unassigning access to')}</span>
        <div css={style.cardRow}>
          <span css={style.cardIdentity}>
            <CardsNewIcon aria-hidden="true" />
            <span css={style.cardName}>{card.cardName}</span>
          </span>
          <MaskedCardNumber lastFour={card.lastFour} />
        </div>
      </section>

      <AlertBanner
        variant="warning"
        icon={<AlertCircleIcon css={style.warningIcon} />}
        data-testid="confirm-unassign-card-invite-note"
      >
        <span>
          {translate(
            'Any pending invitation will remain active and must be cancelled separately in the ${peopleLink} if needed.',
            {
              peopleLink: (
                <HoverableLink
                  to={peopleInvitedToOrgUrl()}
                  ariaLabelSource={'Go to the people page'}
                  testId={'confirm-unassign-card-people-link'}
                  regular
                  underline
                >
                  {translate('people page')}
                </HoverableLink>
              ),
            },
          )}
        </span>
      </AlertBanner>

      {errorMessage && (
        <AlertBanner
          variant="severe"
          icon={<AlertTriangleIcon />}
          title={translate('There was an error unassigning this card')}
          data-testid="confirm-unassign-card-error"
        >
          <span>{errorMessage}</span>
        </AlertBanner>
      )}

      <div css={style.footer}>
        <Button
          variant="no-shell"
          css={style.cancelButton}
          onClick={handleClose}
          disabled={unassignCard.isPending}
          data-testid="confirm-unassign-card-cancel"
        >
          {translate('Cancel')}
        </Button>
        {/* severe: destructive-confirm convention (cf. DisconnectPhysicalCardModal) */}
        <Button
          variant="severe"
          loading={unassignCard.isPending}
          disabled={unassignCard.isPending}
          onClick={handleConfirm}
          data-testid="confirm-unassign-card-confirm"
        >
          {translate('Unassign')}
        </Button>
      </div>
    </BaseModal>
  )
}

export default ConfirmUnassignCardModal
